import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Target, TrendingUp, Users, Award, DollarSign, 
  Loader2, Trophy, Percent, ArrowUp, CheckCircle
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PerformanceDashboard = ({ propertyId, currentTherapistId }) => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    if (propertyId) {
      fetchPerformanceStats();
    }
  }, [propertyId]);

  const fetchPerformanceStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/therapist-performance/monthly-stats/${encodeURIComponent(propertyId)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching performance stats:', error);
      // Don't show error toast, just show empty state
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  if (!stats || stats.monthly_target === 0) {
    return (
      <Card className="bg-muted/30">
        <CardContent className="p-6 text-center">
          <Target className="w-12 h-12 mx-auto mb-3 text-muted-foreground opacity-50" />
          <p className="text-muted-foreground">Monthly target not set for this property</p>
          <p className="text-sm text-muted-foreground mt-1">Contact admin to configure targets</p>
        </CardContent>
      </Card>
    );
  }

  const progressColor = stats.achievement_percent >= 100 
    ? 'bg-green-500' 
    : stats.achievement_percent >= stats.threshold_percent 
      ? 'bg-amber-500' 
      : 'bg-primary';

  return (
    <div className="space-y-4">
      {/* Main Progress Card */}
      <Card className={`${stats.incentive_eligible ? 'border-green-500 bg-green-50/50' : ''}`}>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Target className="w-5 h-5 text-primary" />
                {stats.month_name} Performance
              </CardTitle>
              <CardDescription>Property-wide target & achievement</CardDescription>
            </div>
            {stats.incentive_eligible && (
              <Badge className="bg-green-500 text-white animate-pulse">
                <Award className="w-3 h-3 mr-1" />
                Incentive Unlocked!
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Target vs Achieved */}
          <div className="grid grid-cols-2 gap-4">
            <div className="text-center p-3 bg-muted/30 rounded-lg">
              <p className="text-xs text-muted-foreground uppercase">Monthly Target</p>
              <p className="text-2xl font-bold text-foreground">₹{stats.monthly_target.toLocaleString()}</p>
            </div>
            <div className="text-center p-3 bg-muted/30 rounded-lg">
              <p className="text-xs text-muted-foreground uppercase">Achieved</p>
              <p className={`text-2xl font-bold ${stats.achievement_percent >= stats.threshold_percent ? 'text-green-600' : 'text-foreground'}`}>
                ₹{stats.total_achieved.toLocaleString()}
              </p>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Progress</span>
              <span className="font-medium">{stats.achievement_percent}%</span>
            </div>
            <div className="relative">
              <Progress value={Math.min(stats.achievement_percent, 100)} className="h-4" />
              {/* Threshold marker */}
              <div 
                className="absolute top-0 h-4 w-0.5 bg-red-500"
                style={{ left: `${stats.threshold_percent}%` }}
                title={`${stats.threshold_percent}% Threshold`}
              />
            </div>
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>0%</span>
              <span className="text-red-500 font-medium">{stats.threshold_percent}% threshold</span>
              <span>100%</span>
            </div>
          </div>

          {/* Incentive Info */}
          <div className={`p-3 rounded-lg ${stats.incentive_eligible ? 'bg-green-100 border border-green-300' : 'bg-muted/30'}`}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium">
                  {stats.incentive_eligible ? 'Incentive Earned!' : 'Incentive Target'}
                </p>
                <p className="text-xs text-muted-foreground">
                  {stats.incentive_percentage}% of sales above {stats.threshold_percent}% threshold
                </p>
              </div>
              {stats.incentive_eligible ? (
                <div className="text-right">
                  <p className="text-lg font-bold text-green-600">₹{stats.total_incentive.toLocaleString()}</p>
                  <p className="text-xs text-muted-foreground">Total pool</p>
                </div>
              ) : (
                <div className="text-right">
                  <p className="text-sm text-muted-foreground">
                    ₹{(stats.threshold_amount - stats.total_achieved).toLocaleString()} more to unlock
                  </p>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Therapist Contributions */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <Users className="w-4 h-4 text-primary" />
            Team Contributions
          </CardTitle>
          <CardDescription>
            {stats.active_therapist_count} active therapists • {stats.total_services} total services
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {stats.therapists.map((therapist, index) => {
              const isCurrentUser = therapist.user_id === currentTherapistId;
              return (
                <div 
                  key={therapist.id || index} 
                  className={`flex items-center gap-3 p-2 rounded-lg ${isCurrentUser ? 'bg-primary/10 border border-primary/30' : 'hover:bg-muted/30'}`}
                >
                  {/* Rank Badge */}
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                    index === 0 ? 'bg-yellow-400 text-yellow-900' :
                    index === 1 ? 'bg-gray-300 text-gray-700' :
                    index === 2 ? 'bg-amber-600 text-white' :
                    'bg-muted text-muted-foreground'
                  }`}>
                    {index + 1}
                  </div>
                  
                  {/* Name & Revenue */}
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <p className={`font-medium ${isCurrentUser ? 'text-primary' : ''}`}>
                        {therapist.name}
                        {isCurrentUser && <span className="text-xs ml-1">(You)</span>}
                      </p>
                      {index === 0 && therapist.total_services > 0 && (
                        <Trophy className="w-4 h-4 text-yellow-500" />
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {therapist.total_services} services • {therapist.contribution_percent}% contribution
                    </p>
                  </div>
                  
                  {/* Revenue & Incentive */}
                  <div className="text-right">
                    <p className="font-medium">₹{therapist.total_revenue.toLocaleString()}</p>
                    {therapist.incentive_amount > 0 && (
                      <p className="text-xs text-green-600 flex items-center justify-end gap-1">
                        <Award className="w-3 h-3" />
                        +₹{therapist.incentive_amount.toLocaleString()}
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
            
            {stats.therapists.length === 0 && (
              <p className="text-center text-muted-foreground py-4">No therapists found</p>
            )}
          </div>

          {/* Incentive Distribution Note */}
          {stats.incentive_eligible && stats.per_therapist_incentive > 0 && (
            <div className="mt-4 p-3 bg-green-50 rounded-lg border border-green-200">
              <div className="flex items-center gap-2 text-green-700">
                <CheckCircle className="w-4 h-4" />
                <span className="text-sm font-medium">
                  Each active therapist earns ₹{stats.per_therapist_incentive.toLocaleString()} incentive
                </span>
              </div>
              <p className="text-xs text-green-600 mt-1">
                ₹{stats.amount_above_threshold.toLocaleString()} above threshold × {stats.incentive_percentage}% = ₹{stats.total_incentive.toLocaleString()} ÷ {stats.active_therapist_count} therapists
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PerformanceDashboard;
